import { MapPinIcon } from "lucide-react";
import Image from "next/image";
import { memo } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  isFlightSearchData,
  isHotelSearchData,
  type AiChatPayloadDataByType,
  type AiFlight,
  type AiHotel,
} from "@/feature/ai-planner/types/ai-chat.types";
import FlightRevalidateButton from "@/feature/flight/search/components/shared/flight-revalidate-button";
import { priceFormatter } from "@/lib/price-formatter";
import { absoluteUrl } from "@/lib/utils";
import { ChatHotelAvailabilityButton } from "./chat-hotel-availability-button";
import { ChatHotelImageCarousel } from "./chat-hotel-image-carousel";
import type { ChatMessage } from "../types/chat-ui.types";

type ResultAction = NonNullable<ChatMessage["actions"]>[number];

type ResultContentSharedProps = {
  onAction?: (action: ResultAction) => void;
  actionsDisabled?: boolean;
};

type FlightSearchResultData = AiChatPayloadDataByType<"FLIGHT_SEARCH_RESULT">;
type HotelSearchResultData = AiChatPayloadDataByType<"HOTEL_SEARCH_RESULT">;

function getStopsLabel(stops?: number) {
  if (stops === undefined || stops === null) return "Unknown stops";
  if (stops === 0) return "Non-stop";
  if (stops === 1) return "1 stop";
  return `${stops} stops`;
}

const segmentDateFormatter = new Intl.DateTimeFormat("en-US", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

function formatSegmentDate(date?: string) {
  if (!date) return null;
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  if (!matched) return date;
  const [year, month, day] = matched.slice(1).map(Number);
  const utcDate = new Date(Date.UTC(year, month - 1, day));
  return segmentDateFormatter.format(utcDate);
}

function formatSegmentDateTime(date?: string, time?: string) {
  const formattedDate = formatSegmentDate(date);
  const formattedTime = time?.trim() || "--:--";
  return formattedDate ? `${formattedDate}, ${formattedTime}` : formattedTime;
}

function getRemainingCount(
  totalAvailable: number | undefined,
  loadedCount: number,
) {
  if (typeof totalAvailable === "number" && Number.isFinite(totalAvailable)) {
    return Math.max(0, totalAvailable - loadedCount);
  }
  return 0;
}

const EmptyResultState = memo(function EmptyResultState({
  message,
}: {
  message: string;
}) {
  return (
    <div className="text-muted-foreground mt-3 rounded-xl border border-dashed border-border/80 bg-muted/20 p-3 text-xs">
      {message}
    </div>
  );
});

const ResultFooter = memo(function ResultFooter({
  remainingCount,
  remainingLabel,
  action,
  defaultActionLabel,
  onAction,
  actionsDisabled = false,
}: {
  remainingCount: number;
  remainingLabel: string;
  action?: ResultAction;
  defaultActionLabel: string;
  onAction?: (action: ResultAction) => void;
  actionsDisabled?: boolean;
}) {
  if (!remainingCount && !(action && onAction)) {
    return null;
  }

  return (
    <div className="mt-2 flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-xl border border-border/80 bg-muted/20 px-3 py-2">
      {remainingCount > 0 ? (
        <p className="min-w-0 break-words text-xs text-muted-foreground">
          +{remainingCount} more {remainingLabel}
        </p>
      ) : (
        <span />
      )}
      {action && onAction ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={actionsDisabled}
          className="max-w-full"
          onClick={() => onAction(action)}
        >
          {actionsDisabled
            ? "Loading..."
            : action.label?.trim() || defaultActionLabel}
        </Button>
      ) : null}
    </div>
  );
});

const FlightCard = memo(function FlightCard({
  flight,
  searchId,
}: {
  flight: AiFlight;
  searchId?: string;
}) {
  const airlineLogoUrl = flight.airline_logo
    ? absoluteUrl(flight.airline_logo, true)
    : null;

  return (
    <div className="rounded-xl border border-border/80 bg-card p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2">
            {airlineLogoUrl ? (
              <div className="relative size-6 shrink-0 overflow-hidden rounded-md border border-border/70 bg-background">
                <Image
                  src={airlineLogoUrl}
                  alt={`${flight.airline_name ?? flight.airline_code ?? "Airline"} logo`}
                  fill
                  sizes="24px"
                  className="object-contain p-0.5"
                />
              </div>
            ) : null}
            <p className="truncate font-medium">
              {flight.airline_name ?? flight.airline_code ?? "Airline option"}
            </p>
          </div>
          <p className="text-muted-foreground truncate text-xs">
            {flight.duration ?? "Duration unavailable"} •{" "}
            {getStopsLabel(flight.total_stops)}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="font-semibold">{priceFormatter(flight.price, "BDT")}</p>
          <Badge
            variant={flight.refundable ? "default" : "secondary"}
            className="mt-1"
          >
            {flight.refundable ? "Refundable" : "Non-refundable"}
          </Badge>
          {flight.flight_id && searchId ? (
            <div className="mt-2.5">
              <FlightRevalidateButton
                flight_id={flight.flight_id}
                searchId={searchId}
                disableResponseCheck
              />
            </div>
          ) : null}
        </div>
      </div>

      {flight.segments?.length ? (
        <div className="text-muted-foreground mt-3 space-y-1 border-t border-border/80 pt-3 text-xs">
          {flight.segments.map((segment, index) => (
            <div
              key={`${segment.from}-${segment.to}-${index}`}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2"
            >
              <span className="truncate">
                {segment.from ?? "--"} to {segment.to ?? "--"}
              </span>
              <span className="text-right text-[11px] leading-tight">
                {formatSegmentDateTime(segment.dep_date, segment.dep_time)} -{" "}
                {formatSegmentDateTime(segment.arr_date, segment.arr_time)}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
});

const HotelCard = memo(function HotelCard({
  hotel,
  data,
}: {
  hotel: AiHotel;
  data?: HotelSearchResultData;
}) {
  const facilities = hotel.facilities
    ?.split(",")
    .map((value) => value.trim())
    .filter(Boolean)
    .slice(0, 4);

  return (
    <div className="overflow-hidden rounded-xl border border-border/80 bg-card">
      <div className="grid grid-cols-1 gap-0 sm:grid-cols-[168px_minmax(0,1fr)]">
        <ChatHotelImageCarousel hotel={hotel} />

        <div className="p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-1">
              <p className="break-words font-medium">
                {hotel.name ?? "Hotel option"}
              </p>
              <p className="text-muted-foreground flex items-center gap-1 text-xs">
                <MapPinIcon className="size-3.5 shrink-0" />
                <span className="min-w-0 break-words">
                  {hotel.address ?? "Address unavailable"}
                </span>
              </p>
              <div className="flex flex-wrap gap-1">
                {typeof hotel.category === "number" && hotel.category > 0 ? (
                  <Badge variant="outline">{hotel.category} Star</Badge>
                ) : null}
                <Badge variant={hotel.refundable ? "default" : "secondary"}>
                  {hotel.refundable ? "Refundable" : "Non-refundable"}
                </Badge>
              </div>
            </div>
            <div className="text-right">
              <p className="font-semibold">
                {priceFormatter(hotel.total_price ?? hotel.price, "BDT")}
              </p>
              <p className="text-muted-foreground text-xs">Total</p>
            </div>
          </div>

          {facilities?.length ? (
            <div className="mt-3 flex flex-wrap gap-1 border-t border-border/80 pt-3">
              {facilities.map((facility) => (
                <Badge
                  key={facility}
                  variant="outline"
                  className="max-w-full truncate text-[10px]"
                >
                  {facility}
                </Badge>
              ))}
            </div>
          ) : null}

          {data ? <ChatHotelAvailabilityButton hotel={hotel} data={data} /> : null}
        </div>
      </div>
    </div>
  );
});

const FlightResultsContent = memo(function FlightResultsContent({
  messageId,
  data,
  actions,
  onAction,
  actionsDisabled = false,
}: {
  messageId: string;
  data: FlightSearchResultData;
  actions?: ChatMessage["actions"];
} & ResultContentSharedProps) {
  const flights = data?.flights ?? [];
  const remainingFlights = getRemainingCount(
    data?.total_available,
    flights.length,
  );
  const seeMoreFlightsAction = actions?.find(
    (action) => action.type === "SEE_MORE_FLIGHTS",
  );

  if (!flights.length) {
    return (
      <EmptyResultState message="No flights matched this request. Try another time, airline, or budget." />
    );
  }

  return (
    <section className="mt-3 min-w-0 space-y-2.5">
      {flights.map((flight, index) => (
        <FlightCard
          key={flight.flight_id ?? `flight-${messageId}-${index}`}
          flight={flight}
          searchId={data?.search_id}
        />
      ))}
      <ResultFooter
        remainingCount={remainingFlights}
        remainingLabel="flights"
        action={seeMoreFlightsAction}
        defaultActionLabel="See more flights"
        onAction={onAction}
        actionsDisabled={actionsDisabled}
      />
    </section>
  );
});

const HotelResultsContent = memo(function HotelResultsContent({
  messageId,
  data,
  actions,
  onAction,
  actionsDisabled = false,
}: {
  messageId: string;
  data: HotelSearchResultData;
  actions?: ChatMessage["actions"];
} & ResultContentSharedProps) {
  const hotels = data?.hotels ?? [];
  const remainingHotels = getRemainingCount(
    data?.total_available,
    hotels.length,
  );
  const seeMoreHotelsAction = actions?.find(
    (action) => action.type === "SEE_MORE_HOTELS",
  );

  if (!hotels.length) {
    return (
      <EmptyResultState message="No hotels matched this request. Try changing star rating, facilities, or budget." />
    );
  }

  return (
    <section className="mt-3 min-w-0 space-y-2.5">
      {hotels.map((hotel, index) => (
        <HotelCard
          key={hotel.hotel_code ?? `hotel-${messageId}-${index}`}
          hotel={hotel}
          data={data}
        />
      ))}
      <ResultFooter
        remainingCount={remainingHotels}
        remainingLabel="hotels"
        action={seeMoreHotelsAction}
        defaultActionLabel="See more hotels"
        onAction={onAction}
        actionsDisabled={actionsDisabled}
      />
    </section>
  );
});

export function ChatResultContent({
  message,
  onAction,
  actionsDisabled = false,
  hideContent = false,
}: {
  message: ChatMessage;
  onAction?: (action: ResultAction) => void;
  actionsDisabled?: boolean;
  hideContent?: boolean;
}) {
  if (hideContent || message.type === "NEED_MORE_INFO") {
    return null;
  }

  if (message.type === "FLIGHT_SEARCH_RESULT") {
    const data = isFlightSearchData(message.payload)
      ? message.payload
      : undefined;
    return (
      <FlightResultsContent
        messageId={message.id}
        data={data}
        actions={message.actions}
        onAction={onAction}
        actionsDisabled={actionsDisabled}
      />
    );
  }

  if (message.type === "HOTEL_SEARCH_RESULT") {
    const data = isHotelSearchData(message.payload)
      ? message.payload
      : undefined;
    return (
      <HotelResultsContent
        messageId={message.id}
        data={data}
        actions={message.actions}
        onAction={onAction}
        actionsDisabled={actionsDisabled}
      />
    );
  }

  return null;
}
